import hatAsset from "@/assets/coffman-cove-hat.png.asset.json";
import hoodieAsset from "@/assets/coffman-cove-hoodie.png.asset.json";
import teeAsset from "@/assets/coffman-cove-tee.png.asset.json";
import knifeAsset from "@/assets/coffman-cove-knife.png.asset.json";
import coinAsset from "@/assets/coffman-cove-challenge-coin.png.asset.json";
import mugAsset from "@/assets/coffman-cove-mug.png.asset.json";
import apronAsset from "@/assets/coffman-cove-apron.png.asset.json";

/** Inventory state for a product or a single variant option. */
export type Stock = "in_stock" | "low_stock" | "sold_out";

export type VariantOption = {
  /** Stable value stored on the cart line and the order record. */
  value: string;
  /** Optional display label; defaults to `value`. */
  label?: string;
  /** Defaults to the parent product's stock when omitted. */
  stock?: Stock;
};

export type VariantGroup = {
  /** Stable key, e.g. "size" or "color". */
  id: string;
  label: string;
  options: VariantOption[];
};

/** Selected variant values keyed by group id, e.g. `{ size: "L" }`. */
export type VariantSelection = Record<string, string>;

export type Product = {
  id: string;
  name: string;
  price: number;
  category: "Apparel" | "Gear" | "Keepsake";
  blurb: string;
  description: string[];
  details: string[];
  badge?: string;
  image?: string;
  /** Product-level stock. Defaults to "in_stock" when omitted. */
  stock?: Stock;
  /** Optional size/color pickers shown before add-to-cart. */
  variants?: VariantGroup[];
};

const APPAREL_SIZES: VariantOption[] = [
  { value: "S" },
  { value: "M" },
  { value: "L" },
  { value: "XL" },
  { value: "2XL" },
  { value: "3XL" },
];


export const SHIPPING_NOTE =
  "Every order ships directly to your home anywhere in the lower 48 states — flat rate, tracked, and packed at the lodge. Guests booked with us can also pick up on arrival, no shipping charged.";

export const PRODUCTS: Product[] = [
  {
    id: "tee-navy",
    name: "Cove Logo Tee — Navy",
    price: 35,
    category: "Apparel",
    badge: "Bestseller",
    image: teeAsset.url,
    blurb:
      "Heavyweight navy cotton tee with the Coffman Cove Lodging crest on the chest and full boat-and-mountains print across the back.",
    description: [
      "Our everyday lodge tee, cut from a heavyweight ring-spun cotton that softens with every wash and holds its shape through a season of skiff rides, deck coffee, and campfire nights.",
      "The Coffman Cove Lodging crest sits on the left chest, with the full boat-and-mountains scene printed across the back — the same view guests wake up to every morning on the porch.",
    ],
    details: [
      "6.1 oz heavyweight ring-spun cotton",
      "Classic unisex fit — order your normal size",
      "Screen-printed front crest and full back graphic",
      "Sizes S–3XL",
    ],
    stock: "in_stock",
    variants: [
      { id: "size", label: "Size", options: APPAREL_SIZES },
      {
        id: "color",
        label: "Color",
        options: [
          { value: "navy", label: "Navy" },
          { value: "heather-grey", label: "Heather Grey" },
        ],
      },
    ],
  },
  {
    id: "hoodie",
    name: "Coffman Cove Hoodie",
    price: 75,
    category: "Apparel",
    image: hoodieAsset.url,
    blurb:
      "Heavyweight navy pullover with the Coffman Cove Lodging crest on the left chest and full boat-and-mountains print across the back. Fleece-lined for cold mornings on the deck.",
    description: [
      "The pullover we live in from September to May. Heavy cotton-poly fleece, brushed soft on the inside, with a double-lined hood and thick kangaroo pocket built for cold hands and warm coffee.",
      "Left-chest crest, full-back Coffman Cove scene, and stitched cuffs and hem that hold up to skiff spray and dock grit.",
    ],
    details: [
      "50/50 cotton-poly fleece, 9 oz",
      "Double-lined hood with matched drawcord",
      "Front kangaroo pocket",
      "Sizes S–3XL",
    ],
    stock: "in_stock",
    variants: [
      {
        id: "size",
        label: "Size",
        options: [
          ...APPAREL_SIZES.slice(0, 5),
          { value: "3XL", stock: "sold_out" as const },
        ],
      },
    ],
  },
  {
    id: "cap",
    name: "Coffman Cove Leather Patch Hat",
    price: 35,
    category: "Apparel",
    badge: "New",
    image: hatAsset.url,
    blurb:
      "Classic navy structured cap with a laser-engraved genuine leather patch featuring our Coffman Cove Lodging crest — mountains, boat, and salmon. One-size adjustable back.",
    description: [
      "A proper working hat. Structured six-panel crown, mid-profile, pre-curved brim — sits right the first time you put it on.",
      "The genuine leather patch is laser-engraved with the full Coffman Cove Lodging crest and hand-stitched to the front panel.",
    ],
    details: [
      "Structured six-panel crown, mid-profile",
      "Genuine laser-engraved leather patch",
      "Adjustable snapback closure — one size fits most",
      "Navy with tan patch",
    ],
    stock: "low_stock",
  },
  {
    id: "mug",
    name: "Enamel Camp Mug",
    price: 18,
    category: "Keepsake",
    image: mugAsset.url,
    blurb:
      "Powder-coated stainless steel insulated camp mug with laser-etched Coffman Cove Lodging crest. Sealed sip lid — coffee at sunrise, whiskey at sunset.",
    description: [
      "Double-wall stainless with a powder-coated matte finish that won't chip on the tailgate. Holds heat through a full run out to the halibut grounds.",
      "The Coffman Cove Lodging crest is laser-etched — no sticker, no print, no fading.",
    ],
    details: [
      "12 oz double-wall stainless steel",
      "Powder-coated matte navy exterior",
      "Sealed slide-open sip lid",
      "Hand-wash recommended",
    ],
  },
  {
    id: "knife",
    name: "Filet Knife + Sheath",
    price: 75,
    category: "Gear",
    image: knifeAsset.url,
    blurb:
      "KastKing MadBite 9\" flex fillet knife custom engraved with the Coffman Cove Lodging mark. Includes protective sheath.",
    description: [
      "The knife we hand our guests at the cleaning table. KastKing's MadBite 9-inch flex blade holds an edge through a limit of salmon and slides through halibut cheeks like they aren't there.",
      "Custom-engraved with the Coffman Cove Lodging mark on the blade, non-slip grip handle, and a hard-shell sheath so it travels safely home in your gear bag.",
    ],
    details: [
      "9\" high-carbon stainless flex blade",
      "Non-slip textured TPR handle",
      "Custom Coffman Cove blade engraving",
      "Hard-shell protective sheath included",
    ],
  },
  {
    id: "apron",
    name: "Coffman Cove Logo Apron",
    price: 30,
    category: "Apparel",
    image: apronAsset.url,
    blurb:
      "Heavyweight black canvas apron with adjustable neck strap and dual front pockets. Full-color Coffman Cove Lodging crest printed on the chest — built for the fish-cleaning table, the grill, and the galley.",
    description: [
      "Heavyweight black canvas that wipes clean and holds up to fish scales, grill smoke, and cast-iron nights. Long cut across the chest and thighs — real coverage, not a decorative apron.",
      "Adjustable slide-buckle neck strap, extra-long waist ties, and two deep front pockets for a thermometer, a beer opener, and whatever else the night calls for.",
    ],
    details: [
      "Heavyweight 10 oz cotton canvas, black",
      "Adjustable slide-buckle neck strap",
      "Two deep front pockets",
      "Full-color chest crest — one size fits most",
    ],
    stock: "in_stock",
  },
  {
    id: "challenge-coin",
    name: "Coffman Cove Challenge Coin",
    price: 50,
    category: "Keepsake",
    badge: "Limited",
    image: coinAsset.url,
    blurb:
      "40mm antiqued silver challenge coin. Front: \"It's All About the Fun! — Jerry\" with rod and reel. Back: boat, mountains, and \"Family Owned & Operated for Over 40 Years — Coffman Cove, Alaska.\"",
    description: [
      "A proper heirloom coin, struck in antiqued silver with deep 3D relief on both sides. The front carries Jerry's line — \"It's All About the Fun!\" — around a rod and reel. The back shows the boat, the mountains, and the four-decade mark that Coffman Cove Lodging has been family-owned and operated.",
      "Limited run, individually numbered on the edge. Ships in a felt-lined presentation box.",
    ],
    details: [
      "40mm diameter, antiqued silver finish",
      "Dual-sided 3D relief",
      "Individually edge-numbered — limited run",
      "Felt-lined presentation box included",
    ],
    stock: "low_stock",
  },
];

export const CATEGORIES = ["All", "Apparel", "Gear", "Keepsake"] as const;

export function getProduct(id: string): Product | undefined {
  return PRODUCTS.find((p) => p.id === id);
}

/** Product-level stock, defaulting to in stock. */
export function productStock(product: Product): Stock {
  return product.stock ?? "in_stock";
}

/** Stock for one option, inheriting the product's stock when unset. */
export function optionStock(product: Product, option: VariantOption): Stock {
  return option.stock ?? productStock(product);
}

/** A product is sold out when the product itself is, or every option is. */
export function isSoldOut(product: Product): boolean {
  if (productStock(product) === "sold_out") return true;
  return (product.variants ?? []).some((group) =>
    group.options.every((o) => optionStock(product, o) === "sold_out"),
  );
}

/** Stock for a specific selection — sold out if any chosen option is. */
export function stockForSelection(
  product: Product,
  selection: VariantSelection,
): Stock {
  let stock = productStock(product);
  for (const group of product.variants ?? []) {
    const option = group.options.find((o) => o.value === selection[group.id]);
    if (!option) continue;
    const s = optionStock(product, option);
    if (s === "sold_out") return "sold_out";
    if (s === "low_stock") stock = "low_stock";
  }
  return stock;
}

export function stockLabel(stock: Stock): string {
  if (stock === "sold_out") return "Sold out";
  if (stock === "low_stock") return "Only a few left";
  return "In stock";
}

/** First available option per group — the default pre-selection. */
export function defaultSelection(product: Product): VariantSelection {
  const selection: VariantSelection = {};
  for (const group of product.variants ?? []) {
    const first =
      group.options.find((o) => optionStock(product, o) !== "sold_out") ??
      group.options[0];
    if (first) selection[group.id] = first.value;
  }
  return selection;
}

export function optionLabel(option: VariantOption): string {
  return option.label ?? option.value;
}

/** Human-readable variant summary, e.g. "Size: L · Color: Navy". */
export function describeSelection(
  product: Product,
  selection: VariantSelection | undefined,
): string {
  if (!selection) return "";
  return (product.variants ?? [])
    .map((group) => {
      const option = group.options.find((o) => o.value === selection[group.id]);
      return option ? `${group.label}: ${optionLabel(option)}` : null;
    })
    .filter(Boolean)
    .join(" · ");
}

/** schema.org availability URL matching the product's stock state. */
export function availabilityUrl(product: Product): string {
  const stock = productStock(product);
  if (isSoldOut(product)) return "https://schema.org/OutOfStock";
  return stock === "low_stock"
    ? "https://schema.org/LimitedAvailability"
    : "https://schema.org/InStock";
}



const SITE_ORIGIN = "https://coffmancovelodging.com";

/** Absolute product page URL used by canonical, og:url, JSON-LD. */
export function productUrl(id: string): string {
  return `${SITE_ORIGIN}/shop/${id}`;
}

/** Absolute product image URL for JSON-LD / og:image. Falls back to logo. */
function productImageAbs(product: Product): string {
  const raw =
    product.image ??
    "/__l5e/assets-v1/457538c3-85ec-48b8-9d96-59322c532d66/coffman-cove-logo.png";
  return /^https?:\/\//i.test(raw) ? raw : `${SITE_ORIGIN}${raw}`;
}

/**
 * Schema.org Product markup for a single product page. Includes an Offer
 * block so Google can render price + availability in rich results. Prices
 * are USD, ship from Coffman Cove, AK to the lower 48 — matches the
 * on-page "Ships to your door" copy.
 */
export function productJsonLd(product: Product) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    sku: product.id,
    mpn: `CCL-${product.id.toUpperCase()}`,
    description: product.blurb,
    image: [productImageAbs(product)],
    category: product.category,
    brand: {
      "@type": "Brand",
      name: "Coffman Cove Lodging",
    },
    url: productUrl(product.id),
    offers: {
      "@type": "Offer",
      url: productUrl(product.id),
      priceCurrency: "USD",
      price: product.price.toFixed(2),
      availability: availabilityUrl(product),
      itemCondition: "https://schema.org/NewCondition",
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      seller: {
        "@type": "Organization",
        name: "Coffman Cove Lodging",
      },
    },
  };
}

/**
 * BreadcrumbList JSON-LD for shop pages. `productName` narrows the trail
 * to a specific detail page; omit it on the shop index.
 */
export function shopBreadcrumbsJsonLd(product?: Product) {
  const items = [
    { name: "Home", item: `${SITE_ORIGIN}/` },
    { name: "Shop", item: `${SITE_ORIGIN}/shop` },
  ];
  if (product) {
    items.push({ name: product.name, item: productUrl(product.id) });
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((entry, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

/**
 * ItemList JSON-LD for the shop landing so Google sees every product from
 * one page — better indexation and internal linking signal than only the
 * per-product Product entities.
 */
export function shopItemListJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Coffman Cove Lodging Shop",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    numberOfItems: PRODUCTS.length,
    itemListElement: PRODUCTS.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: productUrl(p.id),
      name: p.name,
    })),
  };
}

